import { useState } from 'react'

import { Box, ScrollContainer, Text } from '@factorialco/deprecated-design-system'
import {
  BulkCreateDocumentsUploadProcessOperationOperationsInput,
  BulkCreateDocumentsUploadProcessOperationsDocument,
  BulkCreateDocumentsUploadProcessOperationsMutation,
  BulkDeleteDocumentsUploadProcessOperationsDocument,
  BulkDeleteDocumentsUploadProcessOperationsMutation,
  DocumentsUploadProcessItemFragment,
  DocumentsUploadProcessOperationItemFragment,
  DocumentsUploadProcessOperationTypesEnum,
  UploadProcessOperationsDocument,
} from 'generated/resources/graphql'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useEffect, useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useManualDocumentAssignerContext } from 'modules/documents/components/BulkFiles/ManualDocumentAssigner/context'

import FileAssigner from './FileAssigner'

type FileData = NonNullable<DocumentsUploadProcessItemFragment['filesData']>[number]

interface Props {
  uploadProcess: DocumentsUploadProcessItemFragment
  filesData: FileData[]
  onClickPageFile: (fileId: number, page: number) => void
}

const FilesAssigner = ({ uploadProcess, filesData, onClickPageFile }: Props) => {
  const context = useManualDocumentAssignerContext()
  const { data: uploadProcessData, loading } = useQuery(UploadProcessOperationsDocument, {
    variables: {
      uploadProcessId: uploadProcess.id,
    },
    fetchPolicy: 'no-cache',
    nextFetchPolicy: 'no-cache',
  })

  const [mutationCreate, { loading: loadingCreate }] = useMutation(
    BulkCreateDocumentsUploadProcessOperationsDocument,
    {
      extractorFn: (data: BulkCreateDocumentsUploadProcessOperationsMutation) =>
        data.documentsMutations.bulkCreateDocumentsUploadProcessOperation,
    }
  )
  const [mutationDelete, { loading: loadingDelete }] = useMutation(
    BulkDeleteDocumentsUploadProcessOperationsDocument,
    {
      extractorFn: (data: BulkDeleteDocumentsUploadProcessOperationsMutation) =>
        data.documentsMutations.bulkDeleteDocumentsUploadProcessOperation,
    }
  )

  const [operations, setOperations] = useState<DocumentsUploadProcessOperationItemFragment[]>([])
  const [operationsPerFile, setOperationsPerFile] = useState<
    DocumentsUploadProcessOperationItemFragment[][]
  >([])

  useEffect(() => {
    context.setLoading(loadingCreate || loadingDelete || loading)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingCreate, loadingDelete, loading])

  useUnsafeEffect(() => {
    setOperations(uploadProcessData?.documents.uploadProcessOperationsConnection.nodes || [])
  }, [uploadProcessData])

  useUnsafeEffect(() => {
    setOperationsPerFile(
      filesData.map((file) => {
        return operations.filter((operation) => operation.fileId === file.fileId)
      })
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operations, filesData])

  const needsMergeOperation = (
    employeeId: number,
    fileId: number,
    page: number,
    direction: string
  ) => {
    const pageToCheck = direction === 'next' ? page + 1 : page - 1
    const fileIndex = filesData.findIndex((file) => file.fileId === fileId)
    return (
      (operationsPerFile[fileIndex]?.filter(
        (op) =>
          op.operationType === 'employee_assignment' &&
          op.page === pageToCheck &&
          op.fileId === fileId &&
          op.targetId === employeeId
      )?.length || 0) > 0
    )
  }

  const createEmployeeAssignmentOperation = (
    employeeId: number,
    fileId: number,
    page: number
  ): BulkCreateDocumentsUploadProcessOperationOperationsInput => {
    const employeeAssignmentType: DocumentsUploadProcessOperationTypesEnum = 'employee_assignment'

    const operation: BulkCreateDocumentsUploadProcessOperationOperationsInput = {
      targetId: employeeId,
      targetType: 'Employee',
      fileId: fileId,
      page,
      operationType: employeeAssignmentType,
    }

    return operation
  }

  const createMergeOperation = (
    fileId: number,
    page: number
  ): BulkCreateDocumentsUploadProcessOperationOperationsInput => {
    const mergeOperationType: DocumentsUploadProcessOperationTypesEnum = 'merge'

    const operation: BulkCreateDocumentsUploadProcessOperationOperationsInput = {
      fileId: fileId,
      page,
      operationType: mergeOperationType,
      targetFileId: fileId,
      targetPage: page - 1,
    }

    return operation
  }

  const createDiscardOperation = (
    fileId: number,
    page: number
  ): BulkCreateDocumentsUploadProcessOperationOperationsInput => {
    const discardOperationType: DocumentsUploadProcessOperationTypesEnum = 'discard'

    const operation: BulkCreateDocumentsUploadProcessOperationOperationsInput = {
      fileId: fileId,
      page,
      operationType: discardOperationType,
    }

    return operation
  }

  const createOperations = async (
    operationsToCreate: BulkCreateDocumentsUploadProcessOperationOperationsInput[]
  ) => {
    const response = await mutationCreate({
      variables: {
        operations: operationsToCreate,
        uploadProcessId: uploadProcess.id,
      },
    })

    const result = handleResult(response, () => undefined)

    if (result.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })

      const newOperations = result?.data?.uploadProcessOperations
      if (newOperations) {
        setOperationsPerFile(updateOperationsPerFile(newOperations))
      }
    }
  }

  const removeOperations = async (ids: number[], fileId: number, skipNotifications = false) => {
    const response = await mutationDelete({
      variables: {
        ids,
      },
    })

    const result = handleResult(response, () => undefined)

    if (result.success) {
      if (!skipNotifications)
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })

      setOperationsPerFile(updateRemoveOperationsPerFile(fileId, ids))
    }
  }

  const updateOperationsPerFile = (
    newOperations: DocumentsUploadProcessOperationItemFragment[]
  ): DocumentsUploadProcessOperationItemFragment[][] => {
    const fileId = newOperations[0]?.fileId
    const fileIndex = filesData.findIndex((file) => file.fileId === fileId)
    const clonedOperations = structuredClone(operationsPerFile)

    return clonedOperations.map((fileOperations, index) => {
      if (index === fileIndex) {
        return [...fileOperations, ...newOperations]
      }
      return fileOperations
    })
  }

  const updateRemoveOperationsPerFile = (
    fileId: number,
    operationIds: number[]
  ): DocumentsUploadProcessOperationItemFragment[][] => {
    const fileIndex = filesData.findIndex((file) => file.fileId === fileId)
    const clonedOperations = structuredClone(operationsPerFile)

    return clonedOperations.map((fileOperations, index) => {
      if (index === fileIndex) {
        return fileOperations.filter((fileOperation) => {
          return !operationIds.includes(fileOperation.id)
        })
      }
      return fileOperations
    })
  }

  const onClickPage = useUnsafeCallback(
    (fileId: number, page: number) => {
      onClickPageFile(fileId, page)
    },
    [onClickPageFile]
  )

  const onEmployeeSelected = useUnsafeCallback(
    (employeeId: number, fileId: number, page: number) => {
      const operationsToCreate: BulkCreateDocumentsUploadProcessOperationOperationsInput[] = []
      operationsToCreate.push(createEmployeeAssignmentOperation(employeeId, fileId, page))
      if (needsMergeOperation(employeeId, fileId, page, 'previous')) {
        operationsToCreate.push(createMergeOperation(fileId, page))
      }
      if (needsMergeOperation(employeeId, fileId, page, 'next')) {
        operationsToCreate.push(createMergeOperation(fileId, page + 1))
      }
      createOperations(operationsToCreate)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [operationsPerFile]
  )

  const onDiscardFilePage = useUnsafeCallback(
    async (fileId: number, page: number) => {
      const ids = allFilePageOperations(fileId, page)
      if (ids.length > 0) await removeOperations(ids, fileId, true)

      createOperations([createDiscardOperation(fileId, page)])
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [operationsPerFile]
  )

  const onRemoveEmployee = useUnsafeCallback(
    (fileId: number, page: number) => {
      const ids = allFilePageOperations(fileId, page)

      if (ids.length === 0) {
        return
      }

      const nextPageMergeOperation = pageMergeOperation(fileId, page + 1)
      if (nextPageMergeOperation) {
        ids.push(nextPageMergeOperation.id)
      }

      removeOperations(ids, fileId)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [operationsPerFile]
  )

  const pageMergeOperation = (fileId: number, page: number) => {
    const fileIndex = filesData.findIndex((file) => file.fileId === fileId)
    return (
      operationsPerFile[fileIndex]
        ?.filter((op) => op.fileId === fileId && op.page === page && op.operationType === 'merge')
        ?.pop() || null
    )
  }

  const allFilePageOperations = (fileId: number, page: number): number[] => {
    const fileIndex = filesData.findIndex((file) => file.fileId === fileId)
    return (
      operationsPerFile[fileIndex]
        ?.filter((op) => op.fileId === fileId && op.page === page)
        ?.map((op) => op.id) || []
    )
  }

  useUnsafeEffect(() => {
    calculatePayslipsToDistribute()
    calculatePayslipsToMerge()
    calculatePayslipsToDiscard()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operationsPerFile, filesData])

  const calculatePayslipsToDistribute = async () => {
    const payslipsToDistribute = operationsPerFile.reduce((acc, fileOperations) => {
      let totalPerFile = fileOperations.filter(
        (op) => op.operationType === 'employee_assignment'
      ).length
      totalPerFile -= fileOperations.filter((op) => op.operationType === 'merge').length

      return totalPerFile + acc
    }, 0)

    context.setPayslipsToDistribute(payslipsToDistribute)
  }

  const calculatePayslipsToMerge = async () => {
    const payslipsToMerge = operationsPerFile.reduce((acc, fileOperations) => {
      return acc + fileOperations.filter((op) => op.operationType === 'merge').length
    }, 0)

    context.setPayslipsToMerge(payslipsToMerge)
  }

  const calculatePayslipsToDiscard = async () => {
    const payslipsToDiscard = operationsPerFile.reduce((acc, fileOperations) => {
      return acc + fileOperations.filter((op) => op.operationType === 'discard').length
    }, 0)

    context.setPayslipsToDiscard(payslipsToDiscard)
  }

  useUnsafeEffect(() => {
    const allPages = filesData.reduce((acc, file) => {
      return acc + (file?.pages || 0)
    }, 0)

    const payslipsNotAssigned =
      allPages -
      (context.payslipsToDistribute || 0) -
      (context.payslipsToDiscard || 0) -
      (context.payslipsToMerge || 0)

    context.setPayslipsNotAssigned(payslipsNotAssigned)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context.payslipsToDiscard, context.payslipsToDistribute, context.payslipsToMerge, filesData])

  const files = useUnsafeMemo(() => {
    return filesData.map((file, index) => {
      return (
        <FileAssigner
          key={file.fileId}
          fileData={file}
          operations={operationsPerFile[index] || []}
          uploadProcess={uploadProcess}
          onClickPageFile={onClickPage}
          onFileEmployeesSelected={onEmployeeSelected}
          onDiscardFilePage={onDiscardFilePage}
          onEnableFilePage={onRemoveEmployee}
          onRemoveEmployee={onRemoveEmployee}
          loading={loading}
        ></FileAssigner>
      )
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operationsPerFile, filesData])

  return (
    <Box height="s600" minWidth="s400">
      <Box
        background="grey200"
        padding="s12"
        gap="s8"
        border={{
          bottom: {
            width: 's1',
            style: 'solid',
            color: 'grey400',
          },
        }}
      >
        <Text size="200" weight="medium">
          {i18n.t('split_documents.workflow_payslips.files_assigner.header')}
        </Text>
      </Box>
      <ScrollContainer>{files}</ScrollContainer>
    </Box>
  )
}

export default FilesAssigner
